<script setup lang="ts">
  import { Radio, Icon, Tag, Typo } from '@travelata-libs/ui-kit';
  import { formatMoney } from '@travelata-libs/utils';
  import { computed, unref } from 'vue';
  import DirectionOptions from './DirectionOptions.vue';
  import { TYPE_TITLES, TYPE_OPTION_TITLES, CAPACITY_ICONS, SEATS_TYPES } from '../lib';
  import {
    type DirectionModelValue,
    DirectionType,
    type DirectionOption,
    type Option,
    type Passengers,
    type Type,
    ChildrenAge,
    Capacity,
  } from '../Transfer.types';

  interface DirectionTypeProps {
    modelValue: DirectionModelValue;
    type: Type;
    passengers: Passengers;
    options: Option[];
  }

  interface DirectionTypeEmits {
    (e: 'change-option', option: DirectionOption): void;
    (e: 'change-type', type: DirectionType): void;
  }

  const props = defineProps<DirectionTypeProps>();

  const emit = defineEmits<DirectionTypeEmits>();

  const innerValue = computed(() => props.modelValue);

  const typeLabel = computed(() => TYPE_TITLES[props.type.value]);

  const selectedOption = computed(() => {
    return props.options.find((option) => option.value === unref(innerValue).option);
  });

  const selectedOptionCapacity = computed(() => {
    const option = unref(selectedOption);

    if (!option) return {};

    return {
      [Capacity.Adult]: props.passengers.adultsCount,
      [Capacity.Children]: props.passengers.childrenCount ?? 0,
      [Capacity.Luggage]: option.luggageCount,
    };
  });

  const capacityIsVisible = computed(
    () => props.type.value === DirectionType.Individual && Object.keys(unref(selectedOptionCapacity)).length > 0,
  );
  const privateDirectionIsAvailable = computed(
    () => props.type.value === DirectionType.Individual && !props.type.disabled,
  );

  const priceInfo = computed(() => {
    if (props.type.disabled) {
      return 'Недоступен';
    }

    if (props.type.includedInPrice) {
      return 'Включен в стоимость';
    }

    const option = unref(selectedOption);
    let price = 0;

    if ([DirectionType.Public, DirectionType.Group].includes(props.type.value)) {
      price = props.type.price || 0;
    } else if (props.type.value === DirectionType.Individual) {
      if (option?.includedInPrice) {
        return 'Включен в стоимость';
      }

      price = option?.price || 0;
    }

    return formatMoney(price);
  });

  const childrenSeats = computed(() => {
    if (props.passengers?.childrenAge?.length) {
      const seats: string[] = [];

      props.passengers?.childrenAge.forEach((age) => {
        if (age <= 1) {
          seats.push(SEATS_TYPES[ChildrenAge.Infant]);
        }
        if (age > 1 && age <= 8) {
          seats.push(SEATS_TYPES[ChildrenAge.Child]);
        }
        if (age > 8 && age <= 14) {
          seats.push(SEATS_TYPES[ChildrenAge.Teenager]);
        }
      });

      return seats;
    }

    return [];
  });

  const infoText = computed(() => {
    const infoList: string[] = [];

    const distanceText =
      props.type.distance || props.type.duration
        ? `В пути ${props.type.distance}${props.type.duration ? ' ≈ ' + props.type.duration : ''}`
        : '';

    switch (props.type.value) {
      case 'public': {
        infoList.push('Вы добираетесь самостоятельно');
        break;
      }

      case 'group': {
        infoList.push('Вас забирает общий с другими туристами автобус');

        if (!props.type.disabled) {
          infoList.push('Встреча с табличкой с названием туроператора');

          if (distanceText) {
            infoList.push(distanceText);
          }
        }
        break;
      }

      case 'individual': {
        if (props.type.disabled) {
          infoList.push('Вас лично забирает автомобиль');

          if (distanceText) {
            infoList.push(distanceText);
          }
        } else {
          infoList.push(TYPE_OPTION_TITLES[unref(innerValue).option], 'Встреча с табличкой с вашими данными');

          if (distanceText) {
            infoList.push(distanceText);
          }

          if (unref(childrenSeats)?.length > 0) {
            infoList.push(...unref(childrenSeats));
          }
        }
        break;
      }
    }

    return infoList.join(' • ');
  });

  const onChangeOption = (option: DirectionOption) => {
    emit('change-option', option);
  };

  const onChangeType = (type: DirectionType) => {
    emit('change-type', type);
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="[
      'tl-transfer-type',
      `tl-transfer-type_${type.value}`,
      {
        'tl-transfer-type_active': innerValue.type === type.value,
        'tl-transfer-type_disabled': type.disabled,
      },
    ]"
  >
    <div class="tl-transfer-type__general">
      <div class="tl-transfer-type__radio-wrapper">
        <Radio
          :model-value="innerValue.type"
          :value="type.value"
          name="transfer-group"
          size="l"
          :disabled="type.disabled || type.loading"
          @update:modelValue="onChangeType"
        >
          <Typo variant="body-l-500" class="tl-transfer-type__label">{{ typeLabel }}</Typo>
        </Radio>

        <Tag size="s" appearance="tertiary" :color="type.disabled ? 'grey' : 'black'" font-weight="medium">
          {{ priceInfo }}
        </Tag>
      </div>

      <DirectionOptions
        v-if="privateDirectionIsAvailable"
        :value="innerValue.option"
        :options="options"
        :info="infoText"
        :loading="type.loading"
        @save="onChangeOption"
      />
    </div>

    <div class="tl-transfer-type__additional">
      <Typo variant="body-m-400">{{ infoText }}</Typo>

      <div v-if="capacityIsVisible" class="tl-transfer-type__capacity">
        <div v-for="(count, name) in selectedOptionCapacity" :key="name" class="tl-transfer-type__capacity-info">
          <Icon class="tl-transfer-type__capacity-icon" :name="CAPACITY_ICONS[name]" size="s" />
          <Typo variant="body-m-500">{{ ` × ${count}` }}</Typo>
        </div>
      </div>
    </div>

    <div v-if="privateDirectionIsAvailable" class="tl-transfer-type__best-choice">
      <Tag size="s" appearance="primary" color="green">Лучший выбор</Tag>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-transfer-type {
    $root: &;

    border-radius: var(--rounding-m);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: var(--spacing-s) var(--spacing-m);
    position: relative;

    @media (max-width: $breakpoint-mobile-max) {
      background-color: var(--color-bg-primary);
      padding: var(--spacing-s);
    }

    &_active {
      background-color: var(--color-sky-05);

      #{$root}__type-label {
        color: var(--color-text-primary);
      }
    }

    &_disabled {
      #{$root}__type-label {
        color: var(--color-text-tertiary);
      }

      #{$root}__additional {
        color: var(--color-text-tertiary);
      }

      &__label {
        color: var(--color-brand-primary);
      }
    }

    &__general {
      display: flex;
      gap: var(--spacing-xs);
      justify-content: space-between;

      #{$root}__radio-wrapper {
        align-items: start;
        display: flex;
        gap: var(--spacing-xs);
      }
    }

    &__additional {
      color: var(--color-text-secondary);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      justify-content: center;
      padding-left: calc(var(--spacing-xs) + var(--spacing-l));
    }

    &__capacity {
      color: var(--color-text-primary);
      display: flex;
      gap: var(--spacing-xs);
    }

    &__capacity-info {
      display: flex;
    }

    &__capacity-icon {
      fill: var(--color-text-secondary);
      flex-shrink: 0;
    }

    &__best-choice {
      left: 0;
      position: absolute;
      top: -26px;

      @media (max-width: $breakpoint-mobile-max) {
        inset: auto var(--spacing-s) var(--spacing-s) auto;
      }
    }
  }
</style>
